/**
 * useThreadMentionAutocomplete — composer @-mention dropdown controller
 * (group-threading P2b follow-up).
 *
 * Watches the contentEditable composer for an in-progress `@query` and drives a
 * ranked member dropdown; on pick it inserts a plain, resolver-safe `@handle` at the
 * caret. Deliberately surgical about the incident-sensitive composer:
 *   • DETECT is read-only — it inspects the live Selection, never mutates.
 *   • INSERT goes through `document.execCommand('insertText', …)`, so the composer's
 *     own `onInput` fires and the draft/prompt state syncs exactly as if the user
 *     typed — no bespoke state writes, no innerHTML surgery.
 *   • Mentions never span text nodes, so detection is node-LOCAL (the caret's text
 *     node only) — robust to whatever rich structure a paste left around it.
 *   • Everything runs off refs and returns `false`/`null` when the flag is off, so
 *     the wrapped `onInput`/`onKeyDown` fall straight through to the existing
 *     handlers and the dropdown never renders.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  detectActiveMention,
  filterMentionCandidates,
  resolveThreadMentionsEnabled,
} from '../../services/threadMentions';
import type { MentionCandidate, MentionRosterEntry } from '../../services/threadMentions';
import { getAccountMembers } from '../../services/prest0nRuntime/accountMembership';
import { loadServerMemberRoster } from '../../services/threadAddPeople/workspaceRoster';
import {
  mergeRosterSources,
  normalizeRosterEntries,
} from '../../services/threadAddPeople/workspaceRosterModel';
import type { MentionAnchorRect } from './ThreadMentionAutocompleteDropdown';

export interface UseThreadMentionAutocompleteArgs {
  accountId: string | null | undefined;
  selfId: string | null | undefined;
  /** The composer's contentEditable ref (shared with the send/draft path). */
  inputRef: React.RefObject<HTMLElement | null>;
  enabledAccountIds?: Iterable<string>;
}

export interface ThreadMentionAutocompleteController {
  enabled: boolean;
  open: boolean;
  candidates: MentionCandidate[];
  activeIndex: number;
  anchorRect: MentionAnchorRect | null;
  /** Wrap the composer's onInput: call after the existing handler. */
  handleInput: () => void;
  /** Wrap the composer's onKeyDown: returns true when it consumed the key. */
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
  onSelect: (index: number) => void;
  onHover: (index: number) => void;
  close: () => void;
}

interface OpenState {
  open: boolean;
  candidates: MentionCandidate[];
  activeIndex: number;
  anchorRect: MentionAnchorRect | null;
}

const CLOSED: OpenState = { open: false, candidates: [], activeIndex: 0, anchorRect: null };

/** Read the caret's node-local text + offset from the live Selection, if inside `el`. */
function readCaret(el: HTMLElement | null): { node: Text; text: string; caret: number } | null {
  if (!el || typeof window === 'undefined') return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const node = sel.focusNode;
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;
  if (!el.contains(node)) return null;
  return { node: node as Text, text: node.textContent ?? '', caret: sel.focusOffset };
}

export function useThreadMentionAutocomplete(
  args: UseThreadMentionAutocompleteArgs,
): ThreadMentionAutocompleteController {
  const accountId = (args.accountId ?? '').trim();
  const selfId = (args.selfId ?? '').trim();
  const { inputRef, enabledAccountIds } = args;

  const enabled = resolveThreadMentionsEnabled({
    accountId: accountId || null,
    enabledAccountIds,
  });

  const [state, setState] = useState<OpenState>(CLOSED);

  // Refs so the wrapped composer handlers stay closure-stable + read live values.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const accountRef = useRef(accountId);
  accountRef.current = accountId;
  const selfRef = useRef(selfId);
  selfRef.current = selfId;
  const stateRef = useRef(state);
  stateRef.current = state;
  const rosterRef = useRef<MentionRosterEntry[]>([]);
  const rosterLoadedRef = useRef(false);

  const close = useCallback(() => {
    setState((s) => (s.open ? CLOSED : s));
  }, []);

  // Recompute the dropdown from the live caret. Read-only; safe to call anytime.
  const recompute = useCallback(() => {
    if (!enabledRef.current) {
      if (stateRef.current.open) close();
      return;
    }
    const el = inputRef.current;
    const caret = readCaret(el);
    if (!caret) {
      close();
      return;
    }
    const active = detectActiveMention(caret.text, caret.caret);
    if (!active) {
      close();
      return;
    }
    const candidates = filterMentionCandidates(rosterRef.current, active.query, selfRef.current);
    if (candidates.length === 0) {
      close();
      return;
    }
    const rect = el?.getBoundingClientRect();
    setState((prev) => ({
      open: true,
      candidates,
      // Keep the highlight if it's still in range; otherwise reset to the top.
      activeIndex: prev.open && prev.activeIndex < candidates.length ? prev.activeIndex : 0,
      anchorRect: rect ? { left: rect.left, top: rect.top, width: rect.width } : prev.anchorRect,
    }));
  }, [close, inputRef]);

  const loadRoster = useCallback(async () => {
    if (rosterLoadedRef.current) return;
    const acct = accountRef.current;
    if (!acct) return;
    rosterLoadedRef.current = true; // guard against concurrent loads
    // TWO SOURCES, SERVER NAMES WINNING (2026-09-08) — the same pair, normalizer
    // and merge the add-people picker uses, so both surfaces name people
    // identically. This used to read member docs from the browser ALONE, and a
    // member doc carries the displayName frozen at invite time: Justin's row in
    // Fernando's workspace still said "Miles Maggio", so the dropdown showed two
    // identical names, no readable handle could be verified as unambiguous, and
    // the composer fell through to a raw uid. `rbacListMembers` joins the live
    // profile, so the server name is the one the person actually set; the
    // Firestore read stays because it works offline and must keep the dropdown
    // alive when the callable fails. Settled independently — one failing must
    // not cost the other's rows.
    const [serverRes, localRes] = await Promise.allSettled([
      loadServerMemberRoster(),
      getAccountMembers(acct, { fromServer: true }),
    ]);
    // Server group FIRST: mergeRosterSources keeps the first non-empty
    // displayName it sees per uid, which is how the server name wins.
    const groups = [];
    if (serverRes.status === 'fulfilled') {
      groups.push(normalizeRosterEntries(serverRes.value.members, 'server'));
    }
    if (localRes.status === 'fulfilled') {
      groups.push(normalizeRosterEntries(localRes.value, 'firestore'));
    }
    if (groups.length === 0) {
      rosterRef.current = [];
      rosterLoadedRef.current = false; // allow a retry on the next mention
    } else {
      // The normalizer already folds `profileEmail` into a blank `email` and
      // drops the platform's own agent identity (2026-09-08).
      rosterRef.current = mergeRosterSources(...groups).map((entry) => ({
        userId: entry.userId,
        ...(entry.displayName ? { displayName: entry.displayName } : {}),
        ...(entry.email ? { email: entry.email } : {}),
      })) as MentionRosterEntry[];
    }
    recompute(); // roster may have arrived after the first "@" — re-run detection
  }, [recompute]);

  // Warm the roster once the flag is on so the first "@" shows members immediately.
  useEffect(() => {
    if (enabled && accountId) void loadRoster();
  }, [enabled, accountId, loadRoster]);

  const handleInput = useCallback(() => {
    if (!enabledRef.current) return;
    if (!rosterLoadedRef.current) void loadRoster();
    recompute();
  }, [loadRoster, recompute]);

  const insertHandle = useCallback(
    (handle: string) => {
      const el = inputRef.current;
      const safe = (handle ?? '').trim();
      if (!el || !safe || typeof window === 'undefined' || typeof document === 'undefined') return;
      const sel = window.getSelection();
      const caret = readCaret(el);
      if (!sel || !caret) return;
      const active = detectActiveMention(caret.text, caret.caret);
      if (!active) return;
      try {
        const range = document.createRange();
        range.setStart(caret.node, active.start);
        range.setEnd(caret.node, active.end);
        sel.removeAllRanges();
        sel.addRange(range);
        // Replaces the "@query" span, fires a native input event (→ composer's
        // onInput → prompt/draft sync), and leaves the caret after the space.
        document.execCommand('insertText', false, `@${safe} `);
      } catch {
        /* best-effort — a failed insert just leaves the user's text untouched */
      }
    },
    [inputRef],
  );

  const onSelect = useCallback(
    (index: number) => {
      const { candidates } = stateRef.current;
      const candidate = candidates[index];
      close();
      if (candidate) insertHandle(candidate.handle);
    },
    [close, insertHandle],
  );

  const onHover = useCallback((index: number) => {
    setState((s) => (s.open && index >= 0 && index < s.candidates.length ? { ...s, activeIndex: index } : s));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!enabledRef.current) return false;
      const s = stateRef.current;
      if (!s.open || s.candidates.length === 0) return false;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setState((p) => ({ ...p, activeIndex: (p.activeIndex + 1) % p.candidates.length }));
          return true;
        case 'ArrowUp':
          e.preventDefault();
          setState((p) => ({
            ...p,
            activeIndex: (p.activeIndex - 1 + p.candidates.length) % p.candidates.length,
          }));
          return true;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          onSelect(s.activeIndex);
          return true;
        case 'Escape':
          e.preventDefault();
          close();
          return true;
        default:
          return false;
      }
    },
    [close, onSelect],
  );

  return {
    enabled,
    open: state.open,
    candidates: state.candidates,
    activeIndex: state.activeIndex,
    anchorRect: state.anchorRect,
    handleInput,
    handleKeyDown,
    onSelect,
    onHover,
    close,
  };
}
